/**
 * /ai/skills/new — bootstrap wizard.
 * Pick 1-8 sample documents from the library → name + lender → the worker
 * runs the analysis model over the PDFs and creates a DRAFT skill (prompt,
 * schema, template, enums, validators, SKILL.md) → guided next steps.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  Input,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Alert,
  AlertIcon,
  Code,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiPlay } from 'react-icons/fi';
import { callDocai, useRunPoller } from '../../hooks/useDocAI';
import StatusChip from '../../components/AI/StatusChip';
import RunProgressBar from '../../components/AI/RunProgressBar';

const NAME_RE = /^[a-z0-9][a-z0-9-]{1,60}$/;

export default function AISkillBootstrapPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [name, setName] = useState('');
  const [lender, setLender] = useState('');
  const [runId, setRunId] = useState(null);
  const { run } = useRunPoller(runId);
  const toast = useToast();
  const history = useHistory();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callDocai('docaiDocuments', {
        action: 'list',
        search: search || undefined,
        limit: 100,
      });
      setDocs(res.documents || []);
    } catch (err) {
      toast({ title: 'Documents load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 8) next.add(id);
      return next;
    });

  const nameOk = NAME_RE.test(name);
  const canStart = nameOk && selected.size >= 1 && !runId;

  const start = async () => {
    try {
      const res = await callDocai('docaiSkills', {
        action: 'bootstrap',
        name,
        lender: lender || null,
        documentIds: [...selected],
      });
      setRunId(res.runId);
      toast({ title: 'Bootstrap started', status: 'info', duration: 3000 });
    } catch (err) {
      toast({ title: 'Bootstrap failed to start', description: err.message, status: 'error', duration: 8000 });
    }
  };

  const done = run?.status === 'done';
  const failed = run?.status === 'error';

  const steps = useMemo(
    () => [
      'Open the draft skill and review the generated prompt / schema / template.',
      'Open 2-3 of the sample documents, correct any wrong fields, and Promote each to golden.',
      'Run eval until it reports 100% field accuracy on the goldens.',
      'Clear the draft flag by saving config with "draft": false (or push via CLI).',
      'Add a taxonomy route (doc type + lender → this skill) so classification routes new uploads here.',
    ],
    []
  );

  return (
    <Box p={6}>
      <HStack mb={4}>
        <Button as={Link} to="/ai/skills" size="sm" variant="ghost" leftIcon={<FiArrowLeft />} />
        <Heading size="md">Bootstrap a new skill</Heading>
      </HStack>

      {!runId && (
        <>
          <HStack mb={4} spacing={3} flexWrap="wrap">
            <Box>
              <Text fontSize="xs" mb={1} color="gray.500">Skill name (kebab-case)</Text>
              <Input
                size="sm"
                w="280px"
                placeholder="e.g. uwm-appraisal"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase())}
                isInvalid={!!name && !nameOk}
              />
            </Box>
            <Box>
              <Text fontSize="xs" mb={1} color="gray.500">Lender / issuer (optional)</Text>
              <Input size="sm" w="200px" placeholder="e.g. UWM" value={lender} onChange={(e) => setLender(e.target.value)} />
            </Box>
            <Box alignSelf="flex-end">
              <Button size="sm" colorScheme="blue" leftIcon={<FiPlay />} isDisabled={!canStart} onClick={start}>
                Analyze {selected.size} sample{selected.size === 1 ? '' : 's'} → draft skill
              </Button>
            </Box>
          </HStack>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchDraft.trim());
            }}
          >
            <HStack mb={3}>
              <Input
                size="sm"
                maxW="320px"
                placeholder="Search documents (file name / loan #)"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
              <Button size="sm" type="submit" variant="outline">Search</Button>
              <Text fontSize="xs" color="gray.500">Pick 1-8 representative PDFs (ideally 3-6, varied layouts)</Text>
            </HStack>
          </form>

          <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
            {loading ? (
              <Center py={16}><Spinner color="blue.400" /></Center>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr><Th w="40px" /><Th>File</Th><Th>Status</Th><Th>Type</Th><Th>Loan #</Th></Tr>
                </Thead>
                <Tbody>
                  {docs.map((d) => (
                    <Tr key={d.id} _hover={{ bg: cardBorder, cursor: 'pointer' }} onClick={() => toggle(d.id)}>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox isChecked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                      </Td>
                      <Td maxW="420px"><Text fontSize="xs" fontFamily="mono" noOfLines={1}>{d.file_name}</Text></Td>
                      <Td><StatusChip status={d.status} /></Td>
                      <Td fontSize="xs">{d.doc_type || '—'}</Td>
                      <Td fontSize="xs" fontFamily="mono">{d.loan_number || '—'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </Box>
        </>
      )}

      {runId && (
        <Box maxW="720px">
          <Box mb={4}><RunProgressBar run={run || { status: 'queued', kind: 'bootstrap' }} /></Box>

          {done && (
            <>
              <Alert status="success" borderRadius="md" mb={4}>
                <AlertIcon />
                Draft skill <Code mx={1}>{name}</Code> created from {selected.size} sample(s).
              </Alert>
              <Heading size="sm" mb={2}>Next steps</Heading>
              <VStack align="stretch" spacing={2} mb={5}>
                {steps.map((s, i) => (
                  <HStack key={i} align="flex-start">
                    <Text fontSize="sm" fontWeight="bold" color="blue.400" w="20px">{i + 1}.</Text>
                    <Text fontSize="sm">{s}</Text>
                  </HStack>
                ))}
              </VStack>
              <HStack>
                <Button as={Link} to={`/ai/skills/${name}`} colorScheme="blue" size="sm">
                  Open draft skill
                </Button>
                <Button as={Link} to="/ai/taxonomy" variant="outline" size="sm">
                  Taxonomy routes
                </Button>
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={4}>
                CLI equivalent: <Code fontSize="xs">docai skills pull {name}</Code> materializes the draft into
                {' '}<Code fontSize="xs">docai/skills/{name}/</Code> for git history + validator editing.
              </Text>
            </>
          )}
          {failed && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Bootstrap failed: {run?.error}
            </Alert>
          )}
          {(done || failed) && (
            <Button mt={4} size="sm" variant="ghost" onClick={() => { setRunId(null); history.push('/ai/skills'); }}>
              Back to skills
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
